import type { FC, ReactNode } from 'react'
import type { ViewerSummary } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Menu, UserRound, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { buttonClass } from '@/components/ui/button'
import { Logo } from '@/components/site/logo'
import { LanguageSwitcher } from '@/components/site/language-switcher'

interface SiteHeaderProps {
    viewer: ViewerSummary | null
    transparent?: boolean
}

export const SiteHeader: FC<SiteHeaderProps> = ({ viewer, transparent = false }): ReactNode => {
    const router = useRouter()
    const { t } = useI18n()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        if (!transparent) return

        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [transparent])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const overlay = transparent && !scrolled && !menuOpen
    const tone = overlay ? 'light' : 'dark'

    const links = [
        { href: '/accommodations', label: t.nav.accommodations },
        { href: '/#about', label: t.nav.about },
        { href: '/booking', label: t.nav.booking },
        { href: '/contact', label: t.nav.contact }
    ]

    const isActive = (href: string) => href !== '/#about' && router.pathname.startsWith(href)

    return (
        <header
            className={cn(
                'inset-x-0 top-0 z-40 transition-colors duration-300',
                transparent ? 'fixed' : 'sticky',
                overlay ? 'bg-transparent' : 'border-b border-border/70 bg-background/90 backdrop-blur-md'
            )}
        >
            <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8'>
                <Logo tone={tone} size='md' />

                <nav aria-label={t.nav.home} className='hidden items-center gap-1 lg:flex'>
                    {links.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            aria-current={isActive(link.href) ? 'page' : undefined}
                            className={cn(
                                'rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-gold',
                                overlay ? 'text-white/90 hover:bg-white/15 hover:text-white' : 'text-navy/80 hover:bg-sand hover:text-navy',
                                isActive(link.href) && (overlay ? 'text-white' : 'bg-sand text-navy')
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-1 sm:gap-2'>
                    <LanguageSwitcher tone={tone} className='hidden sm:block' />

                    <Link
                        href={viewer ? '/account' : '/login'}
                        className={cn(
                            'hidden h-10 items-center gap-2 rounded-full px-3.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-gold md:flex',
                            overlay ? 'text-white hover:bg-white/15' : 'text-navy hover:bg-sand'
                        )}
                    >
                        <UserRound className='size-4' aria-hidden='true' />
                        <span>{viewer ? t.nav.account : t.nav.login}</span>
                    </Link>

                    <Link href='/booking' className={buttonClass({ variant: 'gold', size: 'sm', className: 'hidden sm:inline-flex' })}>
                        {t.common.requestNow}
                    </Link>

                    <button
                        type='button'
                        onClick={() => setMenuOpen(value => !value)}
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? t.nav.closeMenu : t.nav.openMenu}
                        className={cn(
                            'flex size-10 items-center justify-center rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-gold lg:hidden',
                            overlay ? 'text-white hover:bg-white/15' : 'text-navy hover:bg-sand'
                        )}
                    >
                        {menuOpen ? <X className='size-6' aria-hidden='true' /> : <Menu className='size-6' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {menuOpen && (
                <div id='mobile-menu' className='border-t border-border bg-background lg:hidden animate-in fade-in slide-in-from-top-2'>
                    <nav aria-label={t.nav.home} className='mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4 sm:px-6'>
                        {links.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className='rounded-xl px-4 py-3 text-base font-semibold text-navy hover:bg-sand'
                            >
                                {link.label}
                            </Link>
                        ))}

                        <Link href={viewer ? '/account' : '/login'} className='flex items-center gap-2 rounded-xl px-4 py-3 text-base font-semibold text-navy hover:bg-sand'>
                            <UserRound className='size-5' aria-hidden='true' />
                            {viewer ? t.nav.account : t.nav.login}
                        </Link>

                        {!viewer && (
                            <Link href='/register' className='rounded-xl px-4 py-3 text-base font-semibold text-navy hover:bg-sand'>
                                {t.nav.register}
                            </Link>
                        )}

                        <div className='mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-border px-1 pt-4'>
                            <LanguageSwitcher align='start' />
                            <Link href='/booking' className={buttonClass({ variant: 'gold', size: 'md' })}>
                                {t.common.requestNow}
                            </Link>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    )
}
